"use client"

import {
  createContext,
  useContext,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"
import { CircleAlert } from "lucide-react"

import { asChildRender, type AsChildProps } from "@/components/ui/as-child"
import { cn } from "@/lib/utils"

export type TooltipMode = "hint" | "interactive"

const TooltipModeContext = createContext<TooltipMode>("hint")

function HoverTip({ content, children }: { content: ReactNode; children: ReactElement<{ disabled?: boolean }> }) {

  const disabledTrigger = useRef<HTMLSpanElement>(null)
  if (content === undefined || content === null || content === "" || content === false) return children
  return (
    <Tooltip>
      <TooltipTrigger render={children.props.disabled ? <span ref={disabledTrigger} className="inline-flex">{children}</span> : children} />
      <TooltipContent anchor={children.props.disabled ? () => disabledTrigger.current?.firstElementChild ?? null : undefined}>{content}</TooltipContent>
    </Tooltip>
  )
}

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  )
}

function Tooltip({
  mode = "hint",
  disableHoverablePopup,
  ...props
}: TooltipPrimitive.Root.Props & {
  mode?: TooltipMode
}) {
  return (
    <TooltipModeContext.Provider value={mode}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        disableHoverablePopup={
          disableHoverablePopup ?? mode === "hint"
        }
        {...props}
      />
    </TooltipModeContext.Provider>
  )
}

function TooltipTrigger({
  asChild,
  children,
  ...props
}: TooltipPrimitive.Trigger.Props & AsChildProps) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
      {...asChildRender(asChild, children)}
    />
  )
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  anchor,
  variant = "default",
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "anchor"
  > & {

    variant?: "default" | "error"
  }) {
  const mode = useContext(TooltipModeContext)

  const popupColor =
    variant === "error"
      ? "bg-destructive text-white"
      : "bg-tooltip text-tooltip-foreground"
  const arrowColor = variant === "error" ? "bg-destructive" : "bg-tooltip"

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50 data-[anchor-hidden]:hidden"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          data-tooltip-mode={mode}
          className={cn(
            "z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-md px-3 py-1.5 text-xs has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            mode === "interactive" &&
              "max-w-sm cursor-text select-text whitespace-pre-wrap text-left leading-relaxed",
            popupColor,
            className
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow
            className={cn(
              "z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5",
              arrowColor
            )}
          />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

function FieldErrorTooltip({
  open,
  message,
  side = "top",
  align = "start",
  children,
}: {
  open: boolean
  message: string
  side?: "top" | "bottom" | "left" | "right"
  align?: "start" | "center" | "end"
  children: ReactElement
}) {
  return (
    <div className="w-full">
      <Tooltip open={open} mode="hint">
        <TooltipTrigger render={children} />
        <TooltipContent
          variant="error"
          side={side}
          align={align}
          sideOffset={8}
          className="gap-1 font-medium"
        >
          <CircleAlert className="size-3.5 shrink-0" />
          <span>{message}</span>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  FieldErrorTooltip,
  HoverTip,
}
